import React, { useState } from 'react'
import { MdOutlineEmail, MdOutlinePhone, MdWhatsapp } from 'react-icons/md'
import { useBreakpoint } from 'gatsby-plugin-breakpoints'
import { graphql, useStaticQuery } from 'gatsby'
import type { Property } from 'csstype'

import DetailDialog from 'components/AvailableFormsOfService/DetailDialog'
import { classNames } from 'services/string'
import { useAnalytics } from 'hooks'
import WhatsAppButton from 'components/WhatsAppButton'
import { DEFAULT_LIGHT_CONTRAST_COLOR } from 'services/color'

interface FormsOfServiceProps {
  className?: string
  primaryColor?: Property.Color
}

enum FormOfServiceType {
  Phone = 1,
  WhatsApp,
  Email,
}

export interface FormOfService {
  type: FormOfServiceType
  icon: JSX.Element
  label: string
  href?: string | null
  raw?: string | null
  analyticsTarget: string
}

const AvailableFormsOfService = ({ className, primaryColor }: FormsOfServiceProps) => {
  const { fireEvent } = useAnalytics()
  const breakpoints = useBreakpoint()
  const { strapiGlobal } = useStaticQuery(graphql`
    query {
      strapiGlobal {
        ...AvailableFormsOfserviceInfo
      }
    }
  `)

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)
  const [currentFormOfService, setCurrentFormOfService] = useState<FormOfService>()

  const { linkEmail, linkPhone, contactPhone, contactEmail } = strapiGlobal as Queries.AvailableFormsOfserviceInfoFragment
  const availableFormsOfService: FormOfService[] = [
    {
      type: FormOfServiceType.Phone,
      icon: <MdOutlinePhone size='28' />,
      label: 'Telefone',
      href: linkPhone,
      raw: contactPhone,
      analyticsTarget: 'linkPhone',
    },
    {
      type: FormOfServiceType.WhatsApp,
      icon: <MdWhatsapp size='28' />,
      label: 'WhatsApp',
      analyticsTarget: 'linkWhatsApp',
    },
    {
      type: FormOfServiceType.Email,
      icon: <MdOutlineEmail size='28' />,
      label: 'E-mail',
      href: linkEmail,
      raw: contactEmail,
      analyticsTarget: 'linkEmail',
    },
  ]

  // Disparo do evento de clique externo
  const fireAnalyticsEvent = (target: string) => {
    fireEvent('send_contact', { component: 'AvailableFormsOfService', target })

    fireEvent('externalClick', {
      component: 'AvailableFormsOfService',
      target,
    })
  }

  return (
    <div className={classNames(className, 'grid grid-cols-3 gap-x-4')}>
      {availableFormsOfService.map((formOfService, index) => {
        const { icon, label, href, type, analyticsTarget } = formOfService
        return type === FormOfServiceType.WhatsApp ? (
          <WhatsAppButton key={index} onClick={() => fireAnalyticsEvent(analyticsTarget)}>
            <div key={index} className='rounded-xl w-full shadow-lg drop-shadow-lg bg-secondary-50 cursor-pointer'>
              <div
                style={{
                  color: primaryColor
                    ? primaryColor === DEFAULT_LIGHT_CONTRAST_COLOR
                      ? 'var(--text-gray-700)'
                      : primaryColor
                    : 'var(--text-gray-700)',
                }}
                className='flex flex-col items-center justify-center gap-2 px-4 py-2 w-full'
              >
                {icon}
                <p className='text-xs md:text-base'>{label}</p>
              </div>
            </div>
          </WhatsAppButton>
        ) : (
          <div key={index} className='rounded-xl w-full shadow-lg drop-shadow-lg bg-secondary-50'>
            <a
              rel='noreferrer noopener'
              href={href || ''}
              style={{
                color: primaryColor
                  ? primaryColor === DEFAULT_LIGHT_CONTRAST_COLOR
                    ? 'var(--text-gray-700)'
                    : primaryColor
                  : 'var(--text-gray-700)',
              }}
              className='flex flex-col items-center justify-center gap-2 px-4 py-2 w-full'
              onClick={() => {
                fireAnalyticsEvent(analyticsTarget)

                if (!breakpoints.xs) {
                  setCurrentFormOfService(formOfService)
                  setIsDialogOpen(true)
                  return
                }
              }}
            >
              {icon}
              <p className='text-xs md:text-base'>{label}</p>
            </a>
          </div>
        )
      })}

      {currentFormOfService && (
        <DetailDialog
          formOfService={currentFormOfService}
          isOpen={isDialogOpen}
          onClose={() => {
            setIsDialogOpen(false)
            setCurrentFormOfService(undefined)
          }}
        />
      )}
    </div>
  )
}

export const query = graphql`
  fragment AvailableFormsOfserviceInfo on STRAPI_GLOBAL {
    id
    linkEmail
    linkPhone
    contactPhone
    contactEmail
  }
`

export default AvailableFormsOfService
